import { useMemo } from "react";
import Select from "react-select";

const buildLabel = (user, facilityList, includeFacility) => {
    const firstName = user.first_name?.trim() || "";
    const lastName = user.last_name?.trim() || "";
    const nameOrEmail = `${firstName} ${lastName}`.trim() || user.email || user.id;

    if (!includeFacility) return nameOrEmail;

    const userFacilityId = user.facility_id ?? user.facilityId ?? user.facility?.id;
    const userFacilityNumber =
        user.facility_number ?? user.facilityNumber ?? user.facility?.facility_number;

    const facility =
        user.facility?.name
            ? user.facility
            : facilityList.find(
                  (f) =>
                      (userFacilityId != null &&
                          (f.id === userFacilityId ||
                              String(f.id) === String(userFacilityId) ||
                              String(f.facility_number) === String(userFacilityId))) ||
                      (userFacilityNumber != null &&
                          String(f.facility_number) === String(userFacilityNumber)),
              );

    const facilityName = facility?.name || facility?.facility_number;
    return facilityName ? `${facilityName} -> ${nameOrEmail}` : nameOrEmail;
};

export default function UserAccessPicker({
    value = [],
    onChange,
    users = [],
    facilityList = [],
    isSuperadmin = false,
    callerFacilityId = null,
    hasError = false,
    placeholder = "Select users",
    disabled = false,
}) {
    const eligibleUsers = useMemo(() => {
        if (isSuperadmin) return users;
        if (callerFacilityId == null) return users;
        return users.filter((u) => u.facility_id === callerFacilityId);
    }, [users, isSuperadmin, callerFacilityId]);

    const options = useMemo(
        () =>
            eligibleUsers.map((u) => ({
                value: u.id,
                label: buildLabel(u, facilityList, isSuperadmin),
                email: u.email,
            })),
        [eligibleUsers, facilityList, isSuperadmin],
    );

    // Preserve previously-picked users that may not be in the current options
    // list (e.g., superadmin granted a user the admin can't see, or an inactive
    // user that's still on the assistant).
    const selectedOptions = useMemo(() => {
        return value.map((id) => {
            const match = options.find((o) => o.value === id);
            if (match) return match;
            return { value: id, label: id };
        });
    }, [value, options]);

    return (
        <Select
            isMulti
            isDisabled={disabled}
            options={options}
            value={selectedOptions}
            onChange={(vals) => onChange((vals || []).map((v) => v.value))}
            placeholder={placeholder}
            classNamePrefix="react-select"
            closeMenuOnSelect={false}
            filterOption={(option, input) => {
                if (!input) return true;
                const q = input.toLowerCase();
                return (
                    option.label.toLowerCase().includes(q) ||
                    (option.data?.email || "").toLowerCase().includes(q)
                );
            }}
            styles={{
                control: (base, state) => ({
                    ...base,
                    borderRadius: "8px",
                    borderColor: hasError
                        ? "#ef4444"
                        : state.isFocused
                            ? "#3b82f6"
                            : "#e5e7eb",
                    boxShadow: state.isFocused
                        ? "0 0 0 2px rgba(59,130,246,0.25)"
                        : "none",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        />
    );
}
